import React, { useState, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROLES, filterNavigationMenu, applyRoleActions } from './config/rolePermissions';
import { authService } from './api/services/authService';
import { userAccessService } from './api/services/userAccessService';
import { rolePermissionService } from './api/services/rolePermissionService';
import type { AccessibleTenant } from './api/types/auth.types';
import { broadcastLogin } from './utils/authGuard';

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginProps {
  onLoginSuccess?: (user: any) => void;
}

// ============================================================================
// APP CONFIG - Application settings (title, etc.)
// Defined at module level so it can be used for both display and localStorage
// ============================================================================
const appConfig = {
  appTitle: 'LMMS'  // This will appear in header (top-left), login page, and welcome message
};

const LoginInterface: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [isPending, startTransition] = useTransition();
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  // Tenant selection state
  const [tenants, setTenants] = useState<AccessibleTenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [showTenantPicker, setShowTenantPicker] = useState(false);

  const navigate = useNavigate();

  // Real-time email validation (only checks if not empty)
  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    setEmailError('');
    return true;
  };

  // Real-time password validation
  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    }
    setPasswordError('');
    return true;
  };

  // ============================================================================
  // NAVIGATION MENU - Define sidebar menu structure
  // ============================================================================
  const navigationMenu = [
    {
      "section": "Dashboard", "icon": "dashboard",
      "items": [{ "id": "overview", "title": "Overview", "icon": "home", "active": true }]
    },
    {
      "section": "Masters", "icon": "folder",
      "items": [
        { "id": "gold-rate-master", "title": "Gold Rate Master", "icon": "chart" },
        { "id": "customer-registry", "title": "Customer Registry", "icon": "users" }
      ]
    },
    {
      "section": "Transactions", "icon": "file",
      "items": [
        { "id": "addon-requests", "title": "Add-On Requests", "icon": "add" },
        { "id": "loan-mappings", "title": "Loan Mappings", "icon": "link" },
        { "id": "closing-transactions", "title": "Closing Transactions", "icon": "xCircle" },
        { "id": "combined-transactions", "title": "Combined Transactions", "icon": "copy" },
        { "id": "regroup-transactions", "title": "Regroup Transactions", "icon": "refresh" },
        { "id": "gl-replacements", "title": "GL Replacements", "icon": "refresh" }
      ]
    },
    {
      "section": "Approvals", "icon": "checkCircle",
      "items": [{ "id": "approval-dashboard", "title": "Approval Dashboard", "icon": "check" }]
    },
    {
      "section": "Reports", "icon": "trendingUp",
      "items": [
        { "id": "report-addon-summary", "title": "Add-On Summary", "icon": "file" },
        { "id": "report-loan-mapping", "title": "Loan Mapping Report", "icon": "link" },
        { "id": "report-closing-settlement", "title": "Closing Settlement", "icon": "checkCircle" },
        { "id": "report-combined-txn", "title": "Combined Transactions", "icon": "copy" },
        { "id": "report-gl-replacement", "title": "GL Replacements", "icon": "refresh" },
        { "id": "report-audit-trail", "title": "Audit Trail", "icon": "lock" },
        { "id": "report-gold-rates", "title": "Gold Rate History", "icon": "chart" }
      ]
    },
    {
      "section": "Utilities", "icon": "settings",
      "items": [
        { "id": "system-config", "title": "System Configuration", "icon": "settings" },
        { "id": "kfs-calculator", "title": "KFS Calculator", "icon": "calculator" }
      ]
    },
    {
      "section": "Administration", "icon": "lock",
      "items": [
        { "id": "entity-master", "title": "Entity Master", "icon": "folder" },
        { "id": "business-unit-master", "title": "Branch Master", "icon": "home" },
        { "id": "user-bu-access", "title": "User Access", "icon": "users" },
        { "id": "role-permissions", "title": "Role Permissions", "icon": "lock" },
        { "id": "user-management", "title": "User Management", "icon": "user" }
      ]
    }
  ];

  // ============================================================================
  // MODULES CONFIG - Define component mapping for each menu item
  // ============================================================================
  const modulesConfig: Record<string, any> = {
    'gold-rate-master': { showHeader: false, title: 'Gold Rate Master', description: 'Manage gold rates per gram', componentName: 'GoldRateMasterScreen', actions: ['add', 'edit', 'delete', 'activate'], permissions: ['admin', 'manager'], breadcrumb: ['Masters', 'Gold Rate Master'] },
    'customer-registry': { showHeader: false, title: 'Customer Registry', description: 'Manage customer records', componentName: 'CustomerRegistryScreen', actions: ['add', 'edit', 'delete'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Masters', 'Customer Registry'] },
    'addon-requests': { showHeader: false, title: 'Add-On Requests', description: 'Manage add-on loan requests', componentName: 'AddonRequestScreen', actions: ['add', 'edit', 'delete', 'submit'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Transactions', 'Add-On Requests'] },
    'loan-mappings': { showHeader: false, title: 'Loan Mappings', description: 'View approved loan mappings', componentName: 'LoanMappingScreen', actions: ['view'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Transactions', 'Loan Mappings'] },
    'closing-transactions': { showHeader: false, title: 'Closing Transactions', description: 'Manage GL closing transactions', componentName: 'ClosingTransactionScreen', actions: ['add', 'view', 'submit', 'delete'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Transactions', 'Closing Transactions'] },
    'combined-transactions': { showHeader: false, title: 'Combined Transactions', description: 'Manage combined close & new lot transactions', componentName: 'CombinedTransactionScreen', actions: ['add', 'view', 'submit', 'delete'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Transactions', 'Combined Transactions'] },
    'regroup-transactions': { showHeader: false, title: 'Regroup Transactions', description: 'Close an Add-On Loan and regroup GLs into a new lot', componentName: 'RegroupTransactionScreen', actions: ['add', 'edit', 'view', 'submit', 'delete'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Transactions', 'Regroup Transactions'] },
    'gl-replacements': { showHeader: false, title: 'GL Replacements', description: 'Manage GL replacement transactions', componentName: 'GlReplacementScreen', actions: ['add', 'edit', 'view', 'submit', 'delete'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Transactions', 'GL Replacements'] },
    'approval-dashboard': { showHeader: false, title: 'Approval Dashboard', description: 'Pending approval queue', componentName: 'ApprovalDashboardScreen', actions: ['approve', 'return', 'reject'], permissions: ['admin', 'approver'], breadcrumb: ['Approvals', 'Approval Dashboard'] },
    'report-addon-summary': { showHeader: false, title: 'Add-On Summary', description: 'Summary of all add-on requests by status, customer, and date range', componentName: 'AddonSummaryReportScreen', actions: ['view', 'export'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Reports', 'Add-On Summary'] },
    'report-loan-mapping': { showHeader: false, title: 'Loan Mapping Report', description: 'Mapping details with GL allocation breakdown and eligibility calculations', componentName: 'LoanMappingReportScreen', actions: ['view', 'export'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Reports', 'Loan Mapping Report'] },
    'report-closing-settlement': { showHeader: false, title: 'Closing Settlement', description: 'Settlement details for closed loan mappings with P&I breakdown', componentName: 'ClosingSettlementReportScreen', actions: ['view', 'export'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Reports', 'Closing Settlement'] },
    'report-combined-txn': { showHeader: false, title: 'Combined Transactions', description: 'Combined closing and new lot transactions with net cash flow analysis', componentName: 'CombinedTxnReportScreen', actions: ['view', 'export'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Reports', 'Combined Transactions'] },
    'report-gl-replacement': { showHeader: false, title: 'GL Replacements', description: 'GL replacement transactions with weight analysis and settlement breakdown', componentName: 'GlReplacementReportScreen', actions: ['view', 'export'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Reports', 'GL Replacements'] },
    'report-audit-trail': { showHeader: false, title: 'Audit Trail', description: 'Complete audit trail of all approval actions with remarks and timestamps', componentName: 'AuditTrailReportScreen', actions: ['view', 'export'], permissions: ['admin', 'manager', 'supervisor', 'approver'], breadcrumb: ['Reports', 'Audit Trail'] },
    'report-gold-rates': { showHeader: false, title: 'Gold Rate History', description: 'Historical gold rate trends with activation timeline', componentName: 'GoldRateHistoryReportScreen', actions: ['view', 'export'], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Reports', 'Gold Rate History'] },
    'system-config': { showHeader: false, title: 'System Configuration', description: 'Manage system configuration parameters', componentName: 'SystemConfigScreen', actions: ['edit'], permissions: ['admin'], breadcrumb: ['Utilities', 'System Configuration'] },
    'kfs-calculator': { showHeader: false, title: 'KFS Calculator', description: 'Gold Loan Key Fact Statement Calculator', componentName: 'KfsCalculatorScreen', actions: [], permissions: ['admin', 'manager', 'supervisor'], breadcrumb: ['Utilities', 'KFS Calculator'] },
    'entity-master': { showHeader: false, title: 'Entity Master', description: 'Manage companies and legal entities', componentName: 'EntityMasterScreen', actions: ['add', 'edit', 'delete'], permissions: ['admin'], breadcrumb: ['Administration', 'Entity Master'] },
    'business-unit-master': { showHeader: false, title: 'Branch Master', description: 'Manage branches and business units', componentName: 'BusinessUnitMasterScreen', actions: ['add', 'edit', 'delete'], permissions: ['admin'], breadcrumb: ['Administration', 'Branch Master'] },
    'user-bu-access': { showHeader: false, title: 'User Branch Access', description: 'Manage user access to branches', componentName: 'UserBuAccessScreen', actions: ['add', 'edit', 'revoke'], permissions: ['admin'], breadcrumb: ['Administration', 'User Access'] },
    'role-permissions': { showHeader: false, title: 'Role Permissions', description: 'Manage role-based menu and action permissions', componentName: 'RolePermissionScreen', actions: ['add', 'edit', 'delete', 'view'], permissions: ['admin'], breadcrumb: ['Administration', 'Role Permissions'] },
    'user-management': { showHeader: false, title: 'User Management', description: 'Manage user accounts and passwords', componentName: 'UserManagementScreen', actions: ['reset-password'], permissions: ['admin'], breadcrumb: ['Administration', 'User Management'] },
  };

  // Complete authentication: call authenticate, build user session, navigate
  const completeAuthentication = async (tenantId: string) => {
    const authResponse = await authService.authenticate(
      credentials.email,
      credentials.password,
      tenantId
    );

    if (!authResponse.success || !authResponse.data) {
      setError(authResponse.message || 'Authentication failed');
      return;
    }

    const authData = authResponse.data;

    // Build user object for localStorage (consumed by main_layout)
    const displayName = [authData.userFirstName, authData.userLastName].filter(Boolean).join(' ')
      || authData.userName || credentials.email;
    const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    const userRole = authData.userRole || ROLES.USER;

    // Extract Keycloak client roles from the JWT access token
    const extractKeycloakRoles = (token: string): string[] => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload?.resource_access?.lmms?.roles || [];
      } catch {
        return [];
      }
    };

    // Use only Keycloak LMMS client roles (resource_access.lmms.roles) as the
    // authoritative source. TenantHub permissions.roles may contain roles from
    // other applications (e.g. das_user) which must not influence LMMS access.
    const keycloakRoles = authData.accessToken ? extractKeycloakRoles(authData.accessToken) : [];
    // Fallback: if JWT extraction fails, check authData.userRole
    if (keycloakRoles.length === 0 && authData.userRole) {
      keycloakRoles.push(authData.userRole);
    }

    // Role precedence: use only the single highest-priority LMMS role.
    const ROLE_PRIORITY = ['lmms_system', 'lmms_admin', 'lmms_approver', 'lmms_user'];
    const lmmsRoles = keycloakRoles.filter(r => ROLE_PRIORITY.includes(r));
    if (lmmsRoles.length === 0) {
      setError('No LMMS roles are assigned to this account. Please contact the administrator.');
      return;
    }
    const primaryRole = ROLE_PRIORITY.find(r => lmmsRoles.includes(r))!;
    const filterRoles = [primaryRole];

    const user = {
      id: authData.userId || '',
      name: displayName,
      role: primaryRole,
      email: authData.userEmail || credentials.email,
      avatar: initials,
      permissions: [] as string[],
      roles: filterRoles.length > 0 ? filterRoles : [userRole],
    };

    localStorage.setItem('user', JSON.stringify(user));

    // Fetch role permissions from database (source of truth)
    let mergedPermissions: { visibleModules: string[]; moduleActions: Record<string, string[]> };
    try {
      const dbPermissions = await rolePermissionService.getByRoles(filterRoles.join(','));
      if (!dbPermissions.success || !dbPermissions.data) {
        setError('Role permissions are not configured for this account. Please contact the administrator.');
        return;
      }
      if (dbPermissions.data.visibleModules.length === 0) {
        setError('No modules are configured for this role. Please contact the administrator.');
        return;
      }
      mergedPermissions = dbPermissions.data;
    } catch {
      setError('Unable to load permissions. Please try again.');
      return;
    }

    // Apply role-based filtering to navigation menu and module configs
    const filteredNav = filterNavigationMenu(navigationMenu, mergedPermissions.visibleModules);
    const filteredModules = applyRoleActions(modulesConfig, mergedPermissions);
    localStorage.setItem('NAVIGATION_MENU', JSON.stringify(filteredNav));
    localStorage.setItem('MODULES_CONFIG', JSON.stringify(filteredModules));
    localStorage.setItem('APP_CONFIG', JSON.stringify(appConfig));

    // Branch configuration guard — block non-system users without branches
    const isSystemUser = user.roles.includes('lmms_system');
    if (!isSystemUser) {
      try {
        const branchResponse = await userAccessService.getMyBranches();
        const branches = branchResponse.data ?? [];
        if (branches.length === 0) {
          setError('Branch is not configured for the user. Please contact the administrator.');
          return;
        }
      } catch {
        setError('Unable to verify branch configuration. Please try again.');
        return;
      }
    }

    broadcastLogin();
    navigate('../Layout/main_layout');
    onLoginSuccess?.(user);
  };

  // Handle tenant selection from picker
  const handleTenantSelect = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setShowTenantPicker(false);
    setError('');

    startTransition(async () => {
      try {
        await completeAuthentication(tenantId);
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || 'Authentication failed';
        setError(msg);
      }
    });
  };

  const handleSubmit = () => {
    setError('');

    // Validate both fields
    const isEmailValid = validateEmail(credentials.email);
    const isPasswordValid = validatePassword(credentials.password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    startTransition(async () => {
      try {
        // Step 1: Discover accessible tenants
        const discoverResponse = await authService.discover(credentials.email, credentials.password);

        if (!discoverResponse.success || !discoverResponse.data) {
          setError(discoverResponse.message || 'Discovery failed. Please try again.');
          return;
        }

        const { accessibleTenants, recommendedTenant } = discoverResponse.data;

        if (!accessibleTenants || accessibleTenants.length === 0) {
          setError('No accessible tenants found for this application. Please contact your administrator.');
          return;
        }

        // Step 2: Auto-select if single tenant, otherwise show picker
        if (accessibleTenants.length === 1) {
          await completeAuthentication(accessibleTenants[0].tenantId);
        } else {
          // Multiple tenants — show picker
          setTenants(accessibleTenants);
          setSelectedTenantId(recommendedTenant?.tenantId || accessibleTenants[0].tenantId);
          setShowTenantPicker(true);
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || 'Login failed. Please try again.';
        setError(msg);
      }
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation
    if (name === 'email') {
      validateEmail(value);
    } else if (name === 'password') {
      validatePassword(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="lg-root">
      <style>{`
        .lg-root {
          display: flex;
          height: 100vh;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", Roboto, sans-serif;
          --lg-dark: #0f172a;
          --lg-mid: #1e293b;
          --lg-teal: #14b8a6;
          --lg-teal-focus: rgba(20, 184, 166, 0.15);
          --lg-teal-ring: rgba(20, 184, 166, 0.25);
          --lg-slate-50: #f8fafc;
          --lg-slate-100: #f1f5f9;
          --lg-slate-200: #e2e8f0;
          --lg-slate-400: #94a3b8;
          --lg-slate-500: #64748b;
          --lg-slate-700: #334155;
          --lg-slate-900: #0f172a;
          --lg-danger: #ef4444;
          --lg-danger-bg: #fef2f2;
          --lg-danger-border: #fecaca;
        }

        .lg-root * { box-sizing: border-box; }

        /* BRAND PANEL */
        .lg-brand {
          width: 44%;
          flex-shrink: 0;
          background: linear-gradient(155deg, var(--lg-dark) 0%, var(--lg-mid) 100%);
          display: flex;
          flex-direction: column;
          padding: 2.5rem 3rem;
          position: relative;
          overflow: hidden;
          box-shadow: 8px 0 40px rgba(0, 0, 0, 0.3);
          z-index: 1;
        }

        .lg-brand-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          flex-shrink: 0;
          z-index: 1;
        }

        .lg-brand-logo-img {
          height: 88px;
          width: auto;
          max-width: 440px;
          object-fit: contain;
          display: block;
          filter: brightness(0) invert(1);
        }

        .lg-graphic {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2.5rem 0;
          z-index: 1;
        }

        .lg-graphic svg {
          width: 100%;
          max-width: 360px;
          height: auto;
        }

        .lg-hub-ring   { animation: lg-ring-pulse 4s ease-in-out infinite; }
        .lg-hub-ring-2 { animation: lg-ring-pulse 4s ease-in-out infinite 1.5s; }
        .lg-hub-ring-3 { animation: lg-ring-pulse 4s ease-in-out infinite 0.8s; }
        .lg-hub-ring-4 { animation: lg-ring-pulse 4s ease-in-out infinite 2.2s; }
        .lg-flow-a { stroke-dasharray: 6 10; animation: lg-flow 2.4s linear infinite; }
        .lg-flow-b { stroke-dasharray: 6 10; animation: lg-flow 3.1s linear infinite 0.9s; }
        .lg-flow-c { stroke-dasharray: 6 10; animation: lg-flow-rev 2.8s linear infinite 0.4s; }

        .lg-taglines { z-index: 1; flex-shrink: 0; }

        .lg-tagline-h {
          font-size: 1.75rem;
          font-weight: 700;
          color: #ffffff;
          line-height: 1.2;
          letter-spacing: -0.035em;
          margin: 0 0 0.625rem;
        }

        .lg-tagline-p {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.38);
          margin: 0;
          font-weight: 400;
          letter-spacing: 0.005em;
        }

        /* STATS STRIP */
        .lg-stats {
          display: flex;
          align-items: stretch;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 0.75rem;
          margin-bottom: 1.5rem;
          flex-shrink: 0;
          z-index: 1;
          overflow: hidden;
        }

        .lg-stat-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 0.875rem 0.5rem;
          gap: 0.25rem;
        }

        .lg-stat-item + .lg-stat-item {
          border-left: 1px solid rgba(255, 255, 255, 0.08);
        }

        .lg-stat-value {
          font-size: 1.375rem;
          font-weight: 800;
          color: var(--lg-teal);
          letter-spacing: -0.03em;
          line-height: 1;
          white-space: nowrap;
        }

        .lg-stat-label {
          font-size: 0.5625rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.35);
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-align: center;
        }

        /* FEATURE CARDS */
        .lg-features {
          z-index: 1;
          flex-shrink: 0;
          margin-bottom: 2rem;
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .lg-feature-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.07);
          border-radius: 0.625rem;
          padding: 0.625rem 0.75rem;
        }

        .lg-feature-icon {
          width: 2rem;
          height: 2rem;
          border-radius: 0.5rem;
          background: rgba(20, 184, 166, 0.12);
          border: 1px solid rgba(20, 184, 166, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: var(--lg-teal);
        }

        .lg-feature-text {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
          min-width: 0;
        }

        .lg-feature-title {
          font-size: 0.8125rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.82);
          letter-spacing: 0.005em;
          line-height: 1.3;
        }

        .lg-feature-desc {
          font-size: 0.6875rem;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.35);
          letter-spacing: 0.005em;
          line-height: 1.4;
        }

        .lg-brand-footer {
          margin-top: 2rem;
          flex-shrink: 0;
          z-index: 1;
        }

        .lg-version {
          font-size: 0.6875rem;
          color: rgba(255, 255, 255, 0.18);
          font-weight: 500;
          letter-spacing: 0.03em;
          display: block;
          margin-bottom: 0.375rem;
        }

        .lg-brand-website {
          font-size: 0.6875rem;
          font-weight: 600;
          color: rgba(20, 184, 166, 0.65);
          text-decoration: none;
          letter-spacing: 0.02em;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          transition: color 0.2s;
        }

        .lg-brand-website:hover { color: rgba(20, 184, 166, 1); }

        /* APP IDENTITY */
        .lg-app-identity {
          margin-bottom: 1.875rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid var(--lg-slate-200);
        }

        .lg-app-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          background: rgba(20, 184, 166, 0.1);
          border: 1px solid rgba(20, 184, 166, 0.22);
          border-radius: 2rem;
          padding: 0.2rem 0.625rem;
          font-size: 0.625rem;
          font-weight: 700;
          color: #0d9488;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .lg-app-badge-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #14b8a6;
          flex-shrink: 0;
        }

        .lg-app-name {
          font-size: 1.3125rem;
          font-weight: 800;
          color: var(--lg-slate-900);
          letter-spacing: -0.03em;
          line-height: 1.2;
          margin: 0 0 0.5rem;
        }

        /* FORM PANEL */
        .lg-form-panel {
          flex: 1;
          background: #fafbff;
          background-image: radial-gradient(circle, rgba(148, 163, 184, 0.2) 1px, transparent 1px);
          background-size: 22px 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2.5rem 2rem;
          overflow-y: auto;
        }

        .lg-form-inner {
          width: 100%;
          max-width: 400px;
        }

        .lg-form-h1 {
          font-size: 1.625rem;
          font-weight: 700;
          color: var(--lg-slate-900);
          letter-spacing: -0.035em;
          margin: 0 0 0.375rem;
          line-height: 1.2;
        }

        .lg-form-sub {
          font-size: 0.875rem;
          color: var(--lg-slate-500);
          margin: 0 0 2rem;
          font-weight: 400;
          line-height: 1.5;
        }

        /* FIELDS */
        .lg-field { margin-bottom: 1.125rem; }

        .lg-label {
          display: block;
          font-size: 0.6875rem;
          font-weight: 700;
          color: var(--lg-slate-700);
          margin-bottom: 0.4rem;
          letter-spacing: 0.07em;
          text-transform: uppercase;
        }

        .lg-label-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.4rem;
        }

        .lg-label-row .lg-label { margin-bottom: 0; }

        .lg-input-wrap { position: relative; }

        .lg-input-icon {
          position: absolute;
          left: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--lg-slate-400);
          display: flex;
          align-items: center;
          pointer-events: none;
          transition: color 0.2s;
        }

        .lg-input {
          width: 100%;
          padding: 0.6875rem 0.875rem 0.6875rem 2.625rem;
          font-size: 0.875rem;
          font-family: inherit;
          font-weight: 500;
          color: var(--lg-slate-900);
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 0.625rem;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          appearance: none;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
        }

        .lg-input::placeholder { color: var(--lg-slate-400); font-weight: 400; }

        .lg-input:focus {
          border-color: var(--lg-teal);
          box-shadow: 0 0 0 3px var(--lg-teal-focus), 0 1px 3px rgba(15, 23, 42, 0.06);
        }

        .lg-input:focus + .lg-input-icon,
        .lg-input-wrap:focus-within .lg-input-icon { color: var(--lg-teal); }

        .lg-input.has-error {
          border-color: var(--lg-danger);
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .lg-input-pw { padding-right: 2.875rem; }

        .lg-pw-toggle {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: var(--lg-slate-400);
          padding: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.375rem;
          transition: color 0.2s;
          line-height: 0;
        }

        .lg-pw-toggle:hover { color: var(--lg-slate-700); }

        .lg-field-err {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.75rem;
          color: var(--lg-danger);
          margin-top: 0.375rem;
          font-weight: 500;
        }

        .lg-forgot {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--lg-teal);
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          transition: opacity 0.2s;
          font-family: inherit;
        }

        .lg-forgot:hover { opacity: 0.7; }

        /* TENANT PICKER */
        .lg-tenant-list { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; max-height: 260px; overflow-y: auto; }

        .lg-tenant-btn {
          width: 100%;
          text-align: left;
          padding: 0.75rem 0.875rem;
          border: 1.5px solid #cbd5e1;
          border-radius: 0.625rem;
          background: #ffffff;
          cursor: pointer;
          transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
          font-family: inherit;
        }

        .lg-tenant-btn:hover:not(:disabled) { border-color: var(--lg-teal); }
        .lg-tenant-btn.selected { border-color: var(--lg-teal); background: rgba(20, 184, 166, 0.06); box-shadow: 0 0 0 3px var(--lg-teal-focus); }
        .lg-tenant-btn:disabled { cursor: not-allowed; opacity: 0.7; }
        .lg-tenant-name { font-weight: 700; font-size: 0.875rem; color: var(--lg-slate-900); }
        .lg-tenant-code { font-size: 0.75rem; color: var(--lg-slate-500); margin-top: 0.125rem; }

        .lg-back {
          width: 100%;
          padding: 0.6875rem 1rem;
          font-size: 0.8125rem;
          font-weight: 600;
          font-family: inherit;
          color: var(--lg-slate-500);
          background: none;
          border: 1.5px solid var(--lg-slate-200);
          border-radius: 0.625rem;
          cursor: pointer;
          transition: color 0.2s, border-color 0.2s;
        }
        .lg-back:hover:not(:disabled) { color: var(--lg-slate-700); border-color: #cbd5e1; }

        .lg-authing {
          display: flex; align-items: center; justify-content: center; gap: 0.5rem;
          color: #0d9488; font-size: 0.8125rem; font-weight: 600; margin-bottom: 0.75rem;
        }

        /* ERROR ALERT */
        .lg-error-alert {
          display: flex;
          align-items: flex-start;
          gap: 0.625rem;
          background: var(--lg-danger-bg);
          border: 1.5px solid var(--lg-danger-border);
          border-radius: 0.625rem;
          padding: 0.75rem 1rem;
          margin-top: 1rem;
          margin-bottom: 0.25rem;
          color: #dc2626;
          font-size: 0.8125rem;
          font-weight: 500;
          line-height: 1.45;
        }

        .lg-error-icon { flex-shrink: 0; margin-top: 1px; }

        /* SUBMIT BUTTON */
        .lg-submit {
          width: 100%;
          padding: 0.8125rem 1.25rem;
          font-size: 0.9375rem;
          font-weight: 700;
          font-family: inherit;
          letter-spacing: 0.01em;
          border: none;
          border-radius: 0.625rem;
          background: var(--lg-teal);
          color: var(--lg-dark);
          cursor: pointer;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1.375rem;
          position: relative;
          overflow: hidden;
        }

        .lg-submit::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 60%);
          border-radius: inherit;
          pointer-events: none;
        }

        .lg-submit:hover:not(:disabled) {
          background: #0d9488;
          box-shadow: 0 8px 28px rgba(20, 184, 166, 0.4);
          transform: translateY(-1px);
        }

        .lg-submit:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 4px 12px rgba(20, 184, 166, 0.3);
        }

        .lg-submit:disabled {
          background: var(--lg-slate-200);
          color: var(--lg-slate-400);
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .lg-submit:disabled::after { display: none; }

        .lg-spinner {
          width: 17px;
          height: 17px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: lg-spin 0.75s linear infinite;
          flex-shrink: 0;
        }

        .lg-spinner-teal { border: 2px solid rgba(20, 184, 166, 0.3); border-top-color: #0d9488; }

        /* FORM FOOTER */
        .lg-form-footer {
          margin-top: 2rem;
          padding-top: 1.25rem;
          border-top: 1px solid var(--lg-slate-100);
        }

        .lg-footer-links {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-bottom: 0.75rem;
          flex-wrap: wrap;
        }

        .lg-footer-link {
          font-size: 0.75rem;
          color: var(--lg-slate-400);
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }

        .lg-footer-link:hover { color: var(--lg-teal); }

        .lg-footer-link-website { color: var(--lg-teal); font-weight: 600; }
        .lg-footer-link-website:hover { color: #0d9488; }

        .lg-copyright {
          text-align: center;
          font-size: 0.6875rem;
          color: var(--lg-slate-400);
          margin: 0;
          font-weight: 400;
        }

        /* ANIMATIONS */
        @keyframes lg-spin { to { transform: rotate(360deg); } }

        @keyframes lg-ring-pulse {
          0%, 100% { opacity: 0.12; }
          50%      { opacity: 0.28; }
        }

        @keyframes lg-flow {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: -32; }
        }

        @keyframes lg-flow-rev {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: 32; }
        }

        @keyframes lg-enter {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .lg-form-inner { animation: lg-enter 0.55s cubic-bezier(0.16, 1, 0.3, 1) both; }

        /* RESPONSIVE */
        @media (max-width: 1440px) {
          .lg-stats { flex-direction: column; }
          .lg-stat-item {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 0.625rem 0.875rem;
            gap: 0.5rem;
          }
          .lg-stat-item + .lg-stat-item {
            border-left: none;
            border-top: 1px solid rgba(255, 255, 255, 0.08);
          }
          .lg-stat-value { font-size: 0.9375rem; letter-spacing: -0.015em; text-align: left; white-space: nowrap; }
          .lg-stat-label { font-size: 0.5rem; letter-spacing: 0.06em; text-align: right; line-height: 1.25; }
        }

        @media (max-width: 900px) {
          .lg-brand { width: 40%; padding: 1.75rem 1.5rem; }
          .lg-tagline-h { font-size: 1.5rem; }
          .lg-feature-desc { display: none; }
          .lg-feature-item { padding: 0.5rem 0.625rem; }
          .lg-brand-footer { margin-top: 1rem; }
        }

        @media (max-width: 820px) {
          .lg-root {
            flex-direction: column;
            height: auto;
            min-height: 100vh;
            overflow-x: hidden;
          }

          .lg-brand {
            width: 100%;
            flex-direction: row;
            align-items: center;
            padding: 0.875rem 1.375rem;
            flex-shrink: 0;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
          }

          .lg-graphic,
          .lg-stats,
          .lg-features,
          .lg-taglines,
          .lg-brand-footer { display: none; }

          .lg-form-panel { flex: 1; align-items: flex-start; padding: 2rem 1.5rem 2.5rem; }
          .lg-form-inner { max-width: 100%; }
        }

        @media (max-width: 480px) {
          .lg-brand { padding: 0.625rem 1rem; }
          .lg-brand-logo-img { height: 50px; }
          .lg-form-panel { padding: 1.25rem 1rem 1.75rem; }
          .lg-form-h1 { font-size: 1.375rem; }
          .lg-form-sub { font-size: 0.8125rem; margin-bottom: 1.25rem; }
          .lg-app-name { font-size: 1.125rem; }
          .lg-app-identity { margin-bottom: 1.25rem; padding-bottom: 1rem; }
          .lg-footer-links { gap: 0.5rem; }
          .lg-footer-link { font-size: 0.6875rem; }
        }

        @media (max-width: 360px) {
          .lg-brand-logo-img { height: 42px; }
          .lg-form-h1 { font-size: 1.25rem; }
        }
      `}</style>

      {/* LEFT BRAND PANEL */}
      <aside className="lg-brand">
        <div className="lg-brand-logo">
          <img
            src={`${process.env.PUBLIC_URL}/Logos/syut_logo.png`}
            alt="Syut Technologies"
            className="lg-brand-logo-img"
          />
        </div>

        {/* Abstract node-network graphic (decorative) */}
        <div className="lg-graphic">
          <svg viewBox="0 0 460 340" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
              <filter id="lg-glow" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <line x1="40" y1="40" x2="142" y2="55" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="320" y1="38" x2="428" y2="122" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="428" y1="122" x2="444" y2="178" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="444" y1="178" x2="402" y2="232" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="402" y1="232" x2="300" y2="212" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="220" y1="232" x2="234" y2="312" stroke="rgba(148,163,184,0.16)" strokeWidth="1"/>
            <line x1="234" y1="312" x2="356" y2="284" stroke="rgba(148,163,184,0.16)" strokeWidth="1"/>
            <line x1="62" y1="300" x2="234" y2="312" stroke="rgba(148,163,184,0.14)" strokeWidth="1"/>

            <line x1="82" y1="92" x2="40" y2="40" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="82" y1="92" x2="142" y2="55" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="82" y1="92" x2="94" y2="180" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="142" y1="55" x2="192" y2="114" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="320" y2="38" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="428" y2="122" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="252" y2="162" stroke="rgba(20,184,166,0.18)" strokeWidth="1"/>
            <line x1="192" y1="114" x2="252" y2="162" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="252" y2="162" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="356" y2="284" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="156" y2="264" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="252" y1="162" x2="220" y2="232" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="220" y2="232" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="94" y2="180" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="62" y2="300" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="234" y2="312" stroke="rgba(20,184,166,0.18)" strokeWidth="1"/>

            <line x1="82" y1="92" x2="252" y2="162" stroke="rgba(20,184,166,0.55)" strokeWidth="1.5" className="lg-flow-a"/>
            <line x1="252" y1="162" x2="156" y2="264" stroke="rgba(20,184,166,0.55)" strokeWidth="1.5" className="lg-flow-b"/>
            <line x1="378" y1="78" x2="252" y2="162" stroke="rgba(20,184,166,0.45)" strokeWidth="1.5" className="lg-flow-c"/>

            <circle cx="40"  cy="40"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="142" cy="55"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="192" cy="114" r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="320" cy="38"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="428" cy="122" r="3"   fill="rgba(148,163,184,0.4)"/>
            <circle cx="300" cy="212" r="3.5" fill="rgba(148,163,184,0.45)"/>
            <circle cx="402" cy="232" r="3"   fill="rgba(148,163,184,0.38)"/>
            <circle cx="94"  cy="180" r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="220" cy="232" r="3.5" fill="rgba(148,163,184,0.45)"/>
            <circle cx="62"  cy="300" r="2.5" fill="rgba(148,163,184,0.32)"/>
            <circle cx="234" cy="312" r="2.5" fill="rgba(148,163,184,0.32)"/>
            <circle cx="356" cy="284" r="3"   fill="rgba(148,163,184,0.38)"/>
            <circle cx="444" cy="178" r="2.5" fill="rgba(148,163,184,0.32)"/>

            <circle cx="82"  cy="92"  r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring"/>
            <circle cx="252" cy="162" r="18" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-2"/>
            <circle cx="378" cy="78"  r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-3"/>
            <circle cx="156" cy="264" r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-4"/>

            <circle cx="82"  cy="92"  r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="252" cy="162" r="7.5" fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="378" cy="78"  r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="156" cy="264" r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
          </svg>
        </div>

        {/* Stats strip — LMMS-themed */}
        <div className="lg-stats">
          <div className="lg-stat-item">
            <span className="lg-stat-value">Weight-Based</span>
            <span className="lg-stat-label">Eligibility Engine</span>
          </div>
          <div className="lg-stat-item">
            <span className="lg-stat-value">Approval-Gated</span>
            <span className="lg-stat-label">Every Lot Signed Off</span>
          </div>
          <div className="lg-stat-item">
            <span className="lg-stat-value">Multi-Branch</span>
            <span className="lg-stat-label">Scoped by Design</span>
          </div>
        </div>

        {/* Feature cards — LMMS highlights */}
        <div className="lg-features">
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Role-based Access Control</span>
              <span className="lg-feature-desc">Fine-grained permissions per user and branch</span>
            </div>
          </div>
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Gold-Backed Eligibility</span>
              <span className="lg-feature-desc">Headroom from pledged weight &amp; live gold rates</span>
            </div>
          </div>
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 8v4l3 3"/>
                <circle cx="12" cy="12" r="10"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Complete Audit Trail</span>
              <span className="lg-feature-desc">Every submission, approval, and action timestamped</span>
            </div>
          </div>
        </div>

        <div className="lg-taglines">
          <p className="lg-tagline-h">Map every loan.<br/>Settle with confidence.</p>
          <p className="lg-tagline-p">Bringing technology to people.</p>
        </div>

        <div className="lg-brand-footer">
          <span className="lg-version">© {new Date().getFullYear()} Syut Technologies</span>
          <a
            href="https://syuttechnologies.com"
            target="_blank"
            rel="noopener noreferrer"
            className="lg-brand-website"
          >
            syuttechnologies.com
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>
      </aside>

      {/* RIGHT FORM PANEL */}
      <main className="lg-form-panel">
        <div className="lg-form-inner">
          {/* App identity */}
          <div className="lg-app-identity">
            <p className="lg-app-name">{appConfig.appTitle}</p>
            <div className="lg-app-badge">
              <span className="lg-app-badge-dot" />
              Loan Mapping &amp; Management System
            </div>
          </div>

          {showTenantPicker ? (
            /* Tenant Picker */
            <>
              <h1 className="lg-form-h1">Select tenant</h1>
              <p className="lg-form-sub">Choose a tenant to continue signing in</p>

              <div className="lg-tenant-list">
                {tenants.map((tenant) => (
                  <button
                    key={tenant.tenantId}
                    type="button"
                    onClick={() => handleTenantSelect(tenant.tenantId)}
                    disabled={isPending}
                    className={`lg-tenant-btn${selectedTenantId === tenant.tenantId ? ' selected' : ''}`}
                  >
                    <div className="lg-tenant-name">{tenant.tenantDisplayName || tenant.tenantName}</div>
                    <div className="lg-tenant-code">
                      {tenant.tenantCode}{tenant.isPrimaryTenant && ' — Primary'}
                    </div>
                  </button>
                ))}
              </div>

              {isPending && (
                <div className="lg-authing">
                  <div className="lg-spinner lg-spinner-teal" />
                  Authenticating…
                </div>
              )}

              {error && (
                <div className="lg-error-alert">
                  <svg className="lg-error-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10" opacity="0.2"/>
                    <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={() => { setShowTenantPicker(false); setTenants([]); setError(''); }}
                disabled={isPending}
                className="lg-back"
                style={{ marginTop: '1rem' }}
              >
                Back to Login
              </button>
            </>
          ) : (
            <>
              <h1 className="lg-form-h1">Welcome back</h1>
              <p className="lg-form-sub">Sign in to your account to continue</p>

              <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                {/* Email */}
                <div className="lg-field">
                  <label htmlFor="lg-email" className="lg-label">Email Address</label>
                  <div className="lg-input-wrap">
                    <span className="lg-input-icon">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="4" width="20" height="16" rx="2"/>
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                      </svg>
                    </span>
                    <input
                      type="email"
                      id="lg-email"
                      name="email"
                      value={credentials.email}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      disabled={isPending}
                      placeholder="you@company.com"
                      aria-label="Email Address"
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? 'lg-email-err' : undefined}
                      className={`lg-input${emailError ? ' has-error' : ''}`}
                      autoComplete="username"
                    />
                  </div>
                  {emailError && (
                    <p id="lg-email-err" className="lg-field-err">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="10" opacity="0.2"/>
                        <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      {emailError}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div className="lg-field" style={{ marginBottom: '0.5rem' }}>
                  <div className="lg-label-row">
                    <label htmlFor="lg-password" className="lg-label">Password</label>
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => alert('Please contact your administrator to reset your password.')}
                      className="lg-forgot"
                    >
                      Forgot?
                    </button>
                  </div>
                  <div className="lg-input-wrap">
                    <span className="lg-input-icon">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    </span>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="lg-password"
                      name="password"
                      value={credentials.password}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      disabled={isPending}
                      placeholder="Enter your password"
                      aria-label="Password"
                      aria-invalid={!!passwordError}
                      aria-describedby={passwordError ? 'lg-pw-err' : undefined}
                      className={`lg-input lg-input-pw${passwordError ? ' has-error' : ''}`}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="lg-pw-toggle"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {passwordError && (
                    <p id="lg-pw-err" className="lg-field-err">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="10" opacity="0.2"/>
                        <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      {passwordError}
                    </p>
                  )}
                </div>

                {/* Server-side / role-gate error */}
                {error && (
                  <div className="lg-error-alert">
                    <svg className="lg-error-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="12" r="10" opacity="0.2"/>
                      <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    {error}
                  </div>
                )}

                {/* Sign In */}
                <button
                  type="submit"
                  disabled={isPending || !!emailError || !!passwordError}
                  className="lg-submit"
                >
                  {isPending ? (
                    <>
                      <div className="lg-spinner" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      Sign In
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          {/* Footer */}
          <div className="lg-form-footer">
            <div className="lg-footer-links">
              <a href="#privacy" className="lg-footer-link">Privacy Policy</a>
              <a href="#terms" className="lg-footer-link">Terms of Service</a>
              <a href="#support" className="lg-footer-link">Support</a>
              <a
                href="https://syuttechnologies.com"
                target="_blank"
                rel="noopener noreferrer"
                className="lg-footer-link lg-footer-link-website"
              >
                syuttechnologies.com
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '3px', verticalAlign: 'middle' }}>
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            </div>
            <p className="lg-copyright">
              © {new Date().getFullYear()} Syut Technologies. All rights reserved.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginInterface;
